// utils/commonSearchText.ts

/**
 * Normalize string:
 * - lowercase
 * - remove extra spaces
 */
export function normalizeText(text: string): string {
  return text.toLowerCase().trim().replace(/\s+/g, " ");
}

/**
 * Check if a word is loosely matching another word
 * (handles partial + typo tolerance)
 */
function isFuzzyMatch(word: string, target: string): boolean {
  if (target.includes(word)) return true;

  // Allow small typo tolerance (1-2 char diff)
  const distance = levenshteinDistance(word, target);
  return distance <= 2;
}

/**
 * Main fuzzy search function
 */
export function fuzzySearch(query: string, target: string): boolean {
  const normalizedQuery = normalizeText(query);
  const normalizedTarget = normalizeText(target);

  const queryWords = normalizedQuery.split(" ");
  const targetWords = normalizedTarget.split(" ");

  return queryWords.every((qWord) => targetWords.some((tWord) => isFuzzyMatch(qWord, tWord)));
}

/**
 * Levenshtein Distance (for typo tolerance)
 */
function levenshteinDistance(a: string, b: string): number {
  const matrix: number[][] = [];

  for (let i = 0; i <= b.length; i++) matrix[i] = [i];
  for (let j = 0; j <= a.length; j++) matrix[0][j] = j;

  for (let i = 1; i <= b.length; i++) {
    for (let j = 1; j <= a.length; j++) {
      if (b.charAt(i - 1) === a.charAt(j - 1)) {
        matrix[i][j] = matrix[i - 1][j - 1];
      } else {
        matrix[i][j] = Math.min(
          matrix[i - 1][j - 1] + 1, // replace
          matrix[i][j - 1] + 1, // insert
          matrix[i - 1][j] + 1, // delete
        );
      }
    }
  }

  return matrix[b.length][a.length];
}
